import { ITEMS, weaponHold, type WeaponDef } from "./rpg";

export const ITEM_POSE_TYPES = ["rifle", "minigun", "rocket-launcher", "sword", "none"] as const;
export type ItemPoseType = typeof ITEM_POSE_TYPES[number];
export type CharacterStance = "relaxed" | "readied";
/** Visual raise smoothing only; these durations never gate shots. */
export const ITEM_POSE_PROFILES = {
  rifle: { raiseMs: 180, ranged: true, aimSet: "r", hold: "shoulder" },
  minigun: { raiseMs: 300, ranged: true, aimSet: "r", hold: "underhand" },
  "rocket-launcher": { raiseMs: 260, ranged: true, aimSet: "r", hold: "over-shoulder" },
  sword: { raiseMs: 120, ranged: false, aimSet: "sd", hold: "sword" },
  none: { raiseMs: 0, ranged: false, aimSet: null, hold: "free" },
} as const;

/** Visual family derives from catalog hold semantics, never an item-id list. */
export function itemPoseType(weapon?: WeaponDef): ItemPoseType {
  if (!weapon || weapon.construct === "shield") return "none";
  if (weapon.class === "melee") return "sword";
  const hold = weaponHold(weapon).stance;
  return hold === "underhand" ? "minigun" : hold === "over-shoulder" ? "rocket-launcher" : "rifle";
}

export interface CharacterPoseInput {
  itemId: string | null;
  speed: number;
  crouching: boolean;
  grounded: boolean;
  aimHeld: boolean;
  /** Sword target focus can ready the guard without firearm aim input. */
  focused?: boolean;
  blocked?: boolean;
}

export function resolveCharacterPose(input: CharacterPoseInput) {
  const weapon = input.itemId ? ITEMS[input.itemId]?.weapon : undefined;
  const item = itemPoseType(weapon), profile = ITEM_POSE_PROFILES[item];
  const stance: CharacterStance = !input.blocked && (input.aimHeld || (item === "sword" && input.focused)) ? "readied" : "relaxed";
  const aiming = stance === "readied" && profile.ranged;
  return {
    item, stance,
    gait: input.speed < 0.1 ? "idle" as const : input.speed < 3.1 ? "walk" as const : "run" as const,
    posture: input.crouching && input.grounded ? "crouched" as const : "standing" as const,
    airborne: !input.grounded,
    facing: stance === "readied" ? "aim" as const : "travel" as const,
    aiming,
    canFire: !!weapon && weapon.projectileSpeed > 0 && aiming && input.aimHeld,
  };
}
